import type { SagaIterator } from 'redux-saga';
import { call, cancelled, put } from 'redux-saga/effects';
import type { PayloadMetaAction, TypeConstant } from 'typesafe-actions';
import handleError from './handle-error';
import type { BoundRoutine, Requested, Routine } from './new-routines';

export type RoutineSagaOptions = {
  handleBasicErrors: boolean;
  rejectWithFullError: boolean;
};

export function getRoutineSaga<
  Meta,
  RequestedPayload,
  FulfilledPayload,
  Type extends TypeConstant,
  RejectedPayload = string
>(
  routine: Routine<Type, Meta, RequestedPayload, undefined, FulfilledPayload, RejectedPayload, undefined>,
  handler: (payload: RequestedPayload, meta: Meta) => any,
  options: RoutineSagaOptions = {
    handleBasicErrors: true,
    rejectWithFullError: false
  }
) {
  return function* (action: PayloadMetaAction<Requested<Type>, RequestedPayload, Meta>): SagaIterator {
    try {
      yield put(routine.pending(undefined, action.meta));

      const payload = yield call(handler, action.payload, action.meta);
      yield put(routine.fulfilled(payload, action.meta));
      return payload;
    } catch (e) {
      const errorAction = handleError(e);
      if (options.handleBasicErrors && errorAction) {
        yield put(errorAction);
      }

      yield put(routine.rejected(options.rejectWithFullError ? e : e.message, action.meta));
      return null;
    } finally {
      if (yield cancelled()) {
        yield put(routine.cancelled(undefined, action.meta));
      }
    }
  };
}

export function getBoundRoutineSaga<
  Meta,
  RequestedPayload,
  FulfilledPayload,
  Type extends TypeConstant,
  RejectedPayload = string
>(
  routine: BoundRoutine<Type, Meta, RequestedPayload, undefined, FulfilledPayload, RejectedPayload, undefined>,
  handler: (payload: RequestedPayload, meta: Meta) => SagaIterator<FulfilledPayload>,
  options: RoutineSagaOptions = {
    handleBasicErrors: true,
    rejectWithFullError: false
  }
) {
  return function* (action: PayloadMetaAction<Requested<Type>, RequestedPayload, Meta>): SagaIterator {
    try {
      yield put(routine.pending(action.meta)());

      const payload = yield call(handler, action.payload, action.meta);
      yield put(routine.fulfilled(action.meta)(payload));
      return payload;
    } catch (e) {
      const errorAction = handleError(e);
      if (options.handleBasicErrors && errorAction) {
        yield put(errorAction);
      }

      yield put(routine.rejected(action.meta)(options.rejectWithFullError ? e : e.message));
      return null;
    } finally {
      if (yield cancelled()) {
        yield put(routine.cancelled(action.meta)());
      }
    }
  };
}
